import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { SITE } from "@/lib/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeAndScroll = (href) => {
    setOpen(false);
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-2xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="navbar-logo"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group"
        >
          <span
            aria-label="Green Financials logo"
            className="block h-11 w-11 rounded-md"
            style={{
              backgroundImage: `url(${SITE.logo})`,
              backgroundSize: "550%",
              backgroundPosition: "center 28%",
              backgroundRepeat: "no-repeat",
            }}
          />
          <span className="font-serif text-xl tracking-wide text-white group-hover:text-rolex-light transition-colors">
            {SITE.brand}
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {SITE.navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={(e) => {
                e.preventDefault();
                closeAndScroll(l.href);
              }}
              className="text-[11px] uppercase tracking-[0.22em] text-gray-400 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center">
          <a
            href="#calendly-embed"
            onClick={(e) => {
              e.preventDefault();
              closeAndScroll("#calendly-embed");
            }}
            data-testid="navbar-cta-book-consultation"
            className="group inline-flex items-center gap-2 bg-[#006241] hover:bg-[#0a8a5e] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 shadow-[0_0_30px_rgba(0,98,65,0.35)] hover:shadow-[0_0_44px_rgba(0,98,65,0.55)]"
          >
            Book Consultation
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-500 ${
          open ? "max-h-96 border-b border-white/5" : "max-h-0"
        } bg-black/90 backdrop-blur-2xl`}
        data-testid="navbar-mobile-menu"
      >
        <div className="px-6 py-8 flex flex-col gap-6">
          {SITE.navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              onClick={(e) => {
                e.preventDefault();
                closeAndScroll(l.href);
              }}
              className="text-sm uppercase tracking-[0.22em] text-gray-300 hover:text-white"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#calendly-embed"
            onClick={(e) => {
              e.preventDefault();
              closeAndScroll("#calendly-embed");
            }}
            data-testid="mobile-cta-book-consultation"
            className="mt-2 inline-flex items-center justify-center bg-[#006241] hover:bg-[#0a8a5e] text-white px-6 py-3 rounded-full text-sm font-medium"
          >
            Book Consultation
          </a>
        </div>
      </div>
    </header>
  );
}
